import { supabase } from '../lib/supabase';

const serviceManagementService = {
  // ========== SERVICE CRUD OPERATIONS ==========
  
  // Get all services with optional filters and country availability
  getAllServices: async (filters = {}) => {
    try {
      let query = supabase?.from('services')?.select(`
        *,
        service_countries(
          id,
          country_id,
          is_available,
          custom_price,
          regulatory_notes,
          compliance_status,
          certification_required,
          certification_expiry_date,
          country:countries(
            id,
            country_code,
            country_name,
            currency_code
          )
        ),
        package_services(
          id,
          quantity,
          package:commercial_packages(
            id,
            package_name,
            package_code,
            tier
          )
        )
      `);
      
      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }
      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }
      if (filters?.search) {
        query = query?.or(`service_name.ilike.%${filters?.search}%,service_code.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
      }
      
      const { data, error } = await query?.order('service_name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(service => ({
        id: service?.id,
        serviceName: service?.service_name,
        serviceCode: service?.service_code,
        description: service?.description,
        category: service?.category,
        unitPrice: service?.unit_price,
        isActive: service?.is_active,
        createdAt: service?.created_at,
        updatedAt: service?.updated_at,
        availableCountries: service?.service_countries?.map(sc => ({
          id: sc?.id,
          countryId: sc?.country_id,
          countryCode: sc?.country?.country_code,
          countryName: sc?.country?.country_name,
          currencyCode: sc?.country?.currency_code,
          isAvailable: sc?.is_available,
          customPrice: sc?.custom_price,
          regulatoryNotes: sc?.regulatory_notes,
          complianceStatus: sc?.compliance_status,
          certificationRequired: sc?.certification_required,
          certificationExpiryDate: sc?.certification_expiry_date
        })) || [],
        packages: service?.package_services?.map(ps => ({
          id: ps?.package?.id,
          packageName: ps?.package?.package_name,
          packageCode: ps?.package?.package_code,
          tier: ps?.package?.tier,
          quantity: ps?.quantity
        })) || []
      })) || [];
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  },

  // Get single service by ID with full details
  getServiceById: async (id) => {
    try {
      const { data, error } = await supabase?.from('services')?.select(`
        *,
        service_countries(
          id,
          country_id,
          is_available,
          custom_price,
          regulatory_notes,
          compliance_status,
          certification_required,
          certification_expiry_date,
          country:countries(
            id,
            country_code,
            country_name,
            currency_code
          )
        ),
        package_services(
          id,
          quantity,
          package:commercial_packages(
            id,
            package_name,
            package_code,
            tier
          )
        )
      `)?.eq('id', id)?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceName: data?.service_name,
        serviceCode: data?.service_code,
        description: data?.description,
        category: data?.category,
        unitPrice: data?.unit_price,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        availableCountries: data?.service_countries?.map(sc => ({
          id: sc?.id,
          countryId: sc?.country_id,
          countryCode: sc?.country?.country_code,
          countryName: sc?.country?.country_name,
          currencyCode: sc?.country?.currency_code,
          isAvailable: sc?.is_available,
          customPrice: sc?.custom_price,
          regulatoryNotes: sc?.regulatory_notes,
          complianceStatus: sc?.compliance_status,
          certificationRequired: sc?.certification_required,
          certificationExpiryDate: sc?.certification_expiry_date
        })) || [],
        packages: data?.package_services?.map(ps => ({
          id: ps?.package?.id,
          packageName: ps?.package?.package_name,
          packageCode: ps?.package?.package_code,
          tier: ps?.package?.tier,
          quantity: ps?.quantity
        })) || []
      };
    } catch (error) {
      console.error(`Error fetching service ${id}:`, error);
      throw error;
    }
  },

  // Create new service
  createService: async (serviceData) => {
    try {
      const { data, error } = await supabase?.from('services')?.insert({
        service_name: serviceData?.serviceName,
        service_code: serviceData?.serviceCode,
        description: serviceData?.description || null,
        category: serviceData?.category,
        unit_price: serviceData?.unitPrice,
        is_active: serviceData?.isActive !== undefined ? serviceData?.isActive : true
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceName: data?.service_name,
        serviceCode: data?.service_code,
        description: data?.description,
        category: data?.category,
        unitPrice: data?.unit_price,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },

  // Update service
  updateService: async (id, serviceData) => {
    try {
      const updateData = {};
      if (serviceData?.serviceName !== undefined) updateData.service_name = serviceData?.serviceName;
      if (serviceData?.serviceCode !== undefined) updateData.service_code = serviceData?.serviceCode;
      if (serviceData?.description !== undefined) updateData.description = serviceData?.description;
      if (serviceData?.category !== undefined) updateData.category = serviceData?.category;
      if (serviceData?.unitPrice !== undefined) updateData.unit_price = serviceData?.unitPrice;
      if (serviceData?.isActive !== undefined) updateData.is_active = serviceData?.isActive;
      
      updateData.updated_at = new Date()?.toISOString();
      
      const { data, error } = await supabase?.from('services')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceName: data?.service_name,
        serviceCode: data?.service_code,
        description: data?.description,
        category: data?.category,
        unitPrice: data?.unit_price,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating service ${id}:`, error);
      throw error;
    }
  },

  // Delete service
  deleteService: async (id) => {
    try {
      const { error } = await supabase?.from('services')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting service ${id}:`, error);
      throw error;
    }
  },

  // ========== COUNTRY AVAILABILITY OPERATIONS ==========
  
  // Get service availability by country
  getServiceAvailabilityByCountry: async (countryId) => {
    try {
      const { data, error } = await supabase?.from('service_countries')?.select(`
        *,
        service:services(
          id,
          service_name,
          service_code,
          category,
          unit_price,
          is_active
        )
      `)?.eq('country_id', countryId);
      
      if (error) throw error;
      
      return data?.map(sc => ({
        id: sc?.id,
        serviceId: sc?.service_id,
        serviceName: sc?.service?.service_name,
        serviceCode: sc?.service?.service_code,
        category: sc?.service?.category,
        basePrice: sc?.service?.unit_price,
        customPrice: sc?.custom_price,
        isAvailable: sc?.is_available,
        regulatoryNotes: sc?.regulatory_notes,
        complianceStatus: sc?.compliance_status,
        certificationRequired: sc?.certification_required,
        certificationExpiryDate: sc?.certification_expiry_date,
        createdAt: sc?.created_at,
        updatedAt: sc?.updated_at
      })) || [];
    } catch (error) {
      console.error(`Error fetching service availability for country ${countryId}:`, error);
      throw error;
    }
  },

  // Add service to country (make available)
  addServiceToCountry: async (serviceId, countryId, availabilityData = {}) => {
    try {
      const { data, error } = await supabase?.from('service_countries')?.insert({
        service_id: serviceId,
        country_id: countryId,
        is_available: availabilityData?.isAvailable !== undefined ? availabilityData?.isAvailable : true,
        custom_price: availabilityData?.customPrice || null,
        regulatory_notes: availabilityData?.regulatoryNotes || null,
        compliance_status: availabilityData?.complianceStatus || 'compliant',
        certification_required: availabilityData?.certificationRequired || false,
        certification_expiry_date: availabilityData?.certificationExpiryDate || null
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceId: data?.service_id,
        countryId: data?.country_id,
        isAvailable: data?.is_available,
        customPrice: data?.custom_price,
        regulatoryNotes: data?.regulatory_notes,
        complianceStatus: data?.compliance_status,
        certificationRequired: data?.certification_required,
        certificationExpiryDate: data?.certification_expiry_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error adding service to country:', error);
      throw error;
    }
  },

  // Update service availability in country
  updateServiceCountryAvailability: async (id, availabilityData) => {
    try {
      const updateData = {};
      if (availabilityData?.isAvailable !== undefined) updateData.is_available = availabilityData?.isAvailable;
      if (availabilityData?.customPrice !== undefined) updateData.custom_price = availabilityData?.customPrice;
      if (availabilityData?.regulatoryNotes !== undefined) updateData.regulatory_notes = availabilityData?.regulatoryNotes;
      if (availabilityData?.complianceStatus !== undefined) updateData.compliance_status = availabilityData?.complianceStatus;
      if (availabilityData?.certificationRequired !== undefined) updateData.certification_required = availabilityData?.certificationRequired;
      if (availabilityData?.certificationExpiryDate !== undefined) updateData.certification_expiry_date = availabilityData?.certificationExpiryDate;
      
      updateData.updated_at = new Date()?.toISOString();
      
      const { data, error } = await supabase?.from('service_countries')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceId: data?.service_id,
        countryId: data?.country_id,
        isAvailable: data?.is_available,
        customPrice: data?.custom_price,
        regulatoryNotes: data?.regulatory_notes,
        complianceStatus: data?.compliance_status,
        certificationRequired: data?.certification_required,
        certificationExpiryDate: data?.certification_expiry_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating service country availability ${id}:`, error);
      throw error;
    }
  },

  // Remove service from country
  removeServiceFromCountry: async (id) => {
    try {
      const { error } = await supabase?.from('service_countries')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error(`Error removing service from country ${id}:`, error);
      throw error;
    }
  },

  // Bulk update service availability across multiple countries
  bulkUpdateServiceAvailability: async (serviceId, countryUpdates) => {
    try {
      const results = [];
      
      for (const update of countryUpdates) {
        // Check if service-country relationship exists
        const { data: existing } = await supabase?.from('service_countries')
          ?.select('id')
          ?.eq('service_id', serviceId)
          ?.eq('country_id', update?.countryId)
          ?.single();
        
        if (existing) {
          // Update existing
          const result = await serviceManagementService?.updateServiceCountryAvailability(existing?.id, {
            isAvailable: update?.isAvailable,
            customPrice: update?.customPrice,
            complianceStatus: update?.complianceStatus
          });
          results?.push(result);
        } else {
          // Create new
          const result = await serviceManagementService?.addServiceToCountry(serviceId, update?.countryId, {
            isAvailable: update?.isAvailable,
            customPrice: update?.customPrice,
            complianceStatus: update?.complianceStatus
          });
          results?.push(result);
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error bulk updating service availability:', error);
      throw error;
    }
  },

  // Get country availability matrix (all services x all countries)
  getCountryAvailabilityMatrix: async () => {
    try {
      const { data: services, error: servicesError } = await supabase?.from('services')?.select('id, service_name, service_code, category')?.eq('is_active', true);
      const { data: countries, error: countriesError } = await supabase?.from('countries')?.select('id, country_code, country_name')?.eq('is_active', true);
      const { data: availability, error: availabilityError } = await supabase?.from('service_countries')?.select('service_id, country_id, is_available, custom_price, compliance_status');
      
      if (servicesError) throw servicesError;
      if (countriesError) throw countriesError;
      if (availabilityError) throw availabilityError;
      
      // Create matrix
      const matrix = services?.map(service => ({
        serviceId: service?.id,
        serviceName: service?.service_name,
        serviceCode: service?.service_code,
        category: service?.category,
        countries: countries?.map(country => {
          const avail = availability?.find(a => a?.service_id === service?.id && a?.country_id === country?.id);
          return {
            countryId: country?.id,
            countryCode: country?.country_code,
            countryName: country?.country_name,
            isAvailable: avail?.is_available || false,
            customPrice: avail?.custom_price || null,
            complianceStatus: avail?.compliance_status || 'pending'
          };
        })
      }));
      
      return {
        services: services || [],
        countries: countries || [],
        matrix: matrix || []
      };
    } catch (error) {
      console.error('Error fetching country availability matrix:', error);
      throw error;
    }
  },

  // ========== ANALYTICS & REPORTING ==========
  
  // Get service performance analytics
  getServiceAnalytics: async () => {
    try {
      const { data: services, error } = await supabase?.from('services')?.select(`
        id,
        service_name,
        category,
        unit_price,
        is_active,
        service_countries(country_id, is_available),
        package_services(quantity, package:commercial_packages(total_sales, revenue_generated))
      `);
      
      if (error) throw error;
      
      return services?.map(service => {
        const totalCountries = service?.service_countries?.length || 0;
        const availableCountries = service?.service_countries?.filter(sc => sc?.is_available)?.length || 0;
        const totalPackages = service?.package_services?.length || 0;
        const totalRevenue = service?.package_services?.reduce((sum, ps) => {
          return sum + (ps?.package?.revenue_generated || 0);
        }, 0);
        
        return {
          id: service?.id,
          serviceName: service?.service_name,
          category: service?.category,
          unitPrice: service?.unit_price,
          isActive: service?.is_active,
          totalCountries,
          availableCountries,
          coveragePercentage: totalCountries > 0 ? (availableCountries / totalCountries * 100)?.toFixed(1) : 0,
          totalPackages,
          totalRevenue: totalRevenue?.toFixed(2)
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching service analytics:', error);
      throw error;
    }
  }
};

export default serviceManagementService;